import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Pagination from 'react-js-pagination';
import {injectIntl} from 'react-intl';

import Loader from '../dashboard/Loader';
import API from '../../common/utils/API';
import {DateTimeCell, formatDateTime} from './formatDateTime';
import {labelFor, colorForKind} from './factLabels';
import {BandBadge} from './BandBadge';

const KIND_OPTIONS = [
    'booking_created',
    'hold_taken',
    'hold_expired',
    'hold_released',
    'price_changed',
    'offer_listed',
    'waitlist_joined',
];

const CONSISTENT_BADGE = {
    true: {variant: 'success', id: 'RELEVANCE.FACTS_HEADER_CONSISTENT_YES'},
    false: {variant: 'danger', id: 'RELEVANCE.FACTS_HEADER_CONSISTENT_NO'},
    null: {
        variant: 'secondary',
        id: 'RELEVANCE.FACTS_HEADER_CONSISTENT_UNKNOWN',
    },
};

export class EventFactsPanel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            error: false,
            notFound: false,
            eventName: '',
            header: null,
            countsByKind: null,
            rows: [],
            page: 1,
            size: 50,
            total: 0,
            kind: '',
        };

        this.requestSeq = 0;
        this.fetchData = this.fetchData.bind(this);
        this.handleKindChange = this.handleKindChange.bind(this);
        this.handlePageChange = this.handlePageChange.bind(this);
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.eventId !== null &&
            this.props.eventId !== prevProps.eventId
        ) {
            this.setState(
                {
                    page: 1,
                    kind: '',
                    notFound: false,
                    error: false,
                    header: null,
                    countsByKind: null,
                    rows: [],
                    eventName: '',
                },
                this.fetchData,
            );
        }
    }

    async fetchData() {
        if (this.props.eventId === null) {
            return;
        }

        const requestId = ++this.requestSeq;

        this.setState({loading: true});

        try {
            const filters = {page: this.state.page, size: this.state.size};

            if (this.state.kind) {
                filters.kind = this.state.kind;
            }

            const response = await API.reports.getRelevanceEventFacts(
                this.props.eventId,
                filters,
            );

            if (requestId !== this.requestSeq) {
                return;
            }

            this.setState({
                eventName: response.event.name,
                header: response.header,
                countsByKind: response.countsByKind || null,
                rows: response.rows,
                total: response.total,
                loading: false,
                error: false,
                notFound: false,
            });
        } catch (e) {
            if (requestId !== this.requestSeq) {
                return;
            }

            const notFound = Boolean(e.response && e.response.status === 404);

            this.setState({loading: false, error: !notFound, notFound});

            if (notFound) {
                this.props.onHide();
            }
        }
    }

    handleKindChange(event) {
        this.setState({kind: event.target.value, page: 1}, this.fetchData);
    }

    handlePageChange(page) {
        this.setState({page}, this.fetchData);
    }

    renderHeader() {
        const intl = this.props.intl;
        const header = this.state.header;

        if (header === null) {
            return (
                <p data-test='factsNoScore'>
                    {intl.formatMessage({
                        id: 'RELEVANCE.FACTS_HEADER_NO_SCORE',
                    })}
                </p>
            );
        }

        const consistentBadge = CONSISTENT_BADGE[String(header.consistent)];
        const bucketLabel =
            header.bucketKey === '*'
                ? intl.formatMessage({
                      id: 'RELEVANCE.FACTS_HEADER_BASELINE_GLOBAL',
                  })
                : header.bucketKey;

        return (
            <div data-test='factsHeader'>
                <p>
                    {intl.formatMessage(
                        {id: 'RELEVANCE.FACTS_HEADER_RATE'},
                        {
                            demand: header.demand,
                            offeredSeats: header.offeredSeats,
                            rate: header.rate,
                        },
                    )}
                </p>
                <p>
                    {intl.formatMessage(
                        {id: 'RELEVANCE.FACTS_HEADER_BUCKET'},
                        {bucket: bucketLabel},
                    )}
                </p>
                <p data-test='factsHeaderScore'>
                    {intl.formatMessage(
                        {id: 'RELEVANCE.FACTS_HEADER_SCORE'},
                        {
                            score: header.score,
                            band: (
                                <BandBadge
                                    key='band'
                                    intl={intl}
                                    band={header.band}
                                />
                            ),
                            z: header.z,
                        },
                    )}
                </p>
                <p data-test='factsOfferOpened'>
                    {header.offerOpenedAt
                        ? intl.formatMessage(
                              {id: 'RELEVANCE.FACTS_HEADER_OFFER_OPENED'},
                              {
                                  date: formatDateTime(header.offerOpenedAt)
                                      .local,
                              },
                          )
                        : intl.formatMessage({
                              id: 'RELEVANCE.FACTS_HEADER_OFFER_PENDING',
                          })}
                </p>
                <Badge
                    variant={consistentBadge.variant}
                    data-test='factsConsistentBadge'
                >
                    {intl.formatMessage({id: consistentBadge.id})}
                </Badge>
            </div>
        );
    }

    /**
     * Hechos que suman al score, por tipo. Solo los que cuentan, para que el
     * total cuadre con la `n` de la tabla de computados. Salen del backend
     * sobre todos los hechos del evento, así que no cambian con el filtro ni
     * la página. Se listan
     * todos los tipos conocidos (en cero si no hay) y al final los que el
     * catálogo no conoce. Sin el campo (backend anterior) no se muestra.
     */
    renderCountsByKind() {
        const intl = this.props.intl;
        const countsByKind = this.state.countsByKind;

        if (countsByKind === null) {
            return null;
        }

        const byKind = new Map(countsByKind.map((c) => [c.kind, c]));
        const unknownKinds = countsByKind
            .map((c) => c.kind)
            .filter((k) => !KIND_OPTIONS.includes(k));
        const kinds = [...KIND_OPTIONS, ...unknownKinds];
        const sum = countsByKind.reduce((acc, c) => acc + c.counted, 0);

        return (
            <div data-test='factsCountsByKind' className='mb-3'>
                <h6>
                    {intl.formatMessage({id: 'RELEVANCE.FACTS_COUNTS_TITLE'})}
                </h6>
                <Table bordered size='sm' data-test='factsCountsTable'>
                    <thead>
                        <tr>
                            <th>
                                {intl.formatMessage({
                                    id: 'RELEVANCE.FACTS_TABLE_KIND',
                                })}
                            </th>
                            <th className='text-right'>
                                {intl.formatMessage({
                                    id: 'RELEVANCE.FACTS_COUNTS_TOTAL',
                                })}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {kinds.map((kind) => {
                            const total = byKind.has(kind)
                                ? byKind.get(kind).counted
                                : 0;

                            return (
                                <tr
                                    key={kind}
                                    data-test='factsCountsRow'
                                    className={
                                        total === 0 ? 'text-muted' : undefined
                                    }
                                >
                                    <td title={kind}>
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                marginRight: 6,
                                                backgroundColor: colorForKind(
                                                    kind,
                                                ),
                                            }}
                                        />
                                        {labelFor(intl, 'FACT_KIND', kind)}
                                    </td>
                                    <td className='text-right'>{total}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                    <tfoot>
                        <tr data-test='factsCountsTotalRow'>
                            <th>
                                {intl.formatMessage({
                                    id: 'RELEVANCE.FACTS_COUNTS_ALL',
                                })}
                            </th>
                            <th className='text-right'>{sum}</th>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        );
    }

    render() {
        const intl = this.props.intl;
        const {
            loading,
            error,
            notFound,
            rows,
            page,
            size,
            total,
            kind,
        } = this.state;

        return (
            <Modal
                show={this.props.show}
                onHide={this.props.onHide}
                size='lg'
                data-test='eventFactsPanelComponent'
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {intl.formatMessage(
                            {id: 'RELEVANCE.FACTS_PANEL_TITLE'},
                            {name: this.state.eventName},
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{position: 'relative', minHeight: 200}}>
                    <Loader loading={loading} />
                    {notFound ? (
                        <p data-test='factsNotFound'>
                            {intl.formatMessage({
                                id: 'RELEVANCE.FACTS_NOT_FOUND_ERROR',
                            })}
                        </p>
                    ) : error ? (
                        <p data-test='factsError'>
                            {intl.formatMessage({
                                id: 'RELEVANCE.FACTS_LOAD_ERROR',
                            })}
                        </p>
                    ) : (
                        <>
                            {this.renderHeader()}
                            {this.renderCountsByKind()}
                            <Form.Group data-test='factsKindFilter'>
                                <Form.Label>
                                    {intl.formatMessage({
                                        id: 'RELEVANCE.FACTS_KIND_FILTER_LABEL',
                                    })}
                                </Form.Label>
                                <Form.Control
                                    as='select'
                                    value={kind}
                                    onChange={this.handleKindChange}
                                >
                                    <option value=''>
                                        {intl.formatMessage({
                                            id:
                                                'RELEVANCE.FACTS_KIND_FILTER_ALL',
                                        })}
                                    </option>
                                    {KIND_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {labelFor(
                                                intl,
                                                'FACT_KIND',
                                                option,
                                            )}
                                        </option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                            {rows.length === 0 ? (
                                <p data-test='factsEmptyState'>
                                    {intl.formatMessage({
                                        id: 'RELEVANCE.FACTS_EMPTY',
                                    })}
                                </p>
                            ) : (
                                <Table
                                    striped
                                    bordered
                                    hover
                                    size='sm'
                                    data-test='factsTable'
                                >
                                    <thead>
                                        <tr>
                                            <th>
                                                {intl.formatMessage({
                                                    id:
                                                        'RELEVANCE.FACTS_TABLE_OCCURRED_AT',
                                                })}
                                            </th>
                                            <th>
                                                {intl.formatMessage({
                                                    id:
                                                        'RELEVANCE.FACTS_TABLE_KIND',
                                                })}
                                            </th>
                                            <th>
                                                {intl.formatMessage({
                                                    id:
                                                        'RELEVANCE.FACTS_TABLE_MAGNITUDE',
                                                })}
                                            </th>
                                            <th>
                                                {intl.formatMessage({
                                                    id:
                                                        'RELEVANCE.FACTS_TABLE_WEIGHT',
                                                })}
                                            </th>
                                            <th>
                                                {intl.formatMessage({
                                                    id:
                                                        'RELEVANCE.FACTS_TABLE_DECAY',
                                                })}
                                            </th>
                                            <th>
                                                {intl.formatMessage({
                                                    id:
                                                        'RELEVANCE.FACTS_TABLE_CONTRIBUTION',
                                                })}
                                            </th>
                                            <th>
                                                {intl.formatMessage({
                                                    id:
                                                        'RELEVANCE.FACTS_TABLE_STATUS',
                                                })}
                                            </th>
                                            <th>
                                                {intl.formatMessage({
                                                    id:
                                                        'RELEVANCE.FACTS_TABLE_SOURCE',
                                                })}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <tr
                                                key={row.id}
                                                data-test='factsRow'
                                                className={
                                                    row.status === 'excluded'
                                                        ? 'text-muted'
                                                        : undefined
                                                }
                                            >
                                                <td>
                                                    <DateTimeCell
                                                        iso={row.occurredAt}
                                                    />
                                                </td>
                                                <td title={row.kind}>
                                                    {labelFor(
                                                        intl,
                                                        'FACT_KIND',
                                                        row.kind,
                                                    )}
                                                </td>
                                                <td>{row.magnitude}</td>
                                                <td>{row.weight}</td>
                                                <td>{row.decay}</td>
                                                <td>{row.contribution}</td>
                                                <td>
                                                    {row.status === 'excluded'
                                                        ? `${labelFor(
                                                              intl,
                                                              'FACT_STATUS',
                                                              row.status,
                                                          )} (${labelFor(
                                                              intl,
                                                              'FACT_REASON',
                                                              row.reason,
                                                          )})`
                                                        : labelFor(
                                                              intl,
                                                              'FACT_STATUS',
                                                              row.status,
                                                          )}
                                                </td>
                                                <td>
                                                    {row.kind ===
                                                    'booking_created' ? (
                                                        <a
                                                            href={`/bookings/${row.sourceId}`}
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                            data-test='factsSourceLink'
                                                        >
                                                            {row.sourceId}
                                                        </a>
                                                    ) : (
                                                        <Button
                                                            variant='link'
                                                            size='sm'
                                                            data-test='factsCopySourceId'
                                                            onClick={() =>
                                                                navigator.clipboard &&
                                                                navigator.clipboard.writeText(
                                                                    String(
                                                                        row.sourceId,
                                                                    ),
                                                                )
                                                            }
                                                        >
                                                            {row.sourceId}
                                                        </Button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                            {total > size && (
                                <div className='d-flex justify-content-end'>
                                    <Pagination
                                        itemClass='page-item'
                                        linkClass='page-link'
                                        activePage={page}
                                        itemsCountPerPage={size}
                                        totalItemsCount={total}
                                        pageRangeDisplayed={5}
                                        onChange={this.handlePageChange}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant='secondary'
                        onClick={this.fetchData}
                        data-test='factsRefreshButton'
                    >
                        {intl.formatMessage({id: 'RELEVANCE.REFRESH_BUTTON'})}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default injectIntl(EventFactsPanel);
